import {
  Button,
  Divider,
  Grid,
  Group,
  Modal,
  type ModalProps,
  ScrollArea,
  Select,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useListState } from "@mantine/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import CheckboxGroup from "src/components/shared/_FormInputs/CheckboxGroup/CheckboxGroup";
import { usePostApi } from "src/hooks";
import { Flex } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import AddCheckListButton from "../AddCheckListButton";
import { ensureItemIds } from "../CheckList.utils";
import {
  CheckListType,
  type ICheckListForm,
  type ICheckListItem,
} from "../CheckLists.types";
import { useChecklistsContext } from "../CheckListsContext";
import CheckListFieldArray from "./CheckListFieldArray";
import ChecklistGroupItem from "./ChecklistGroupItem";
import styles from "./CheckListModal.module.scss";

interface Props extends ModalProps {
  defaultValues?: ICheckListForm & { cid: string };
  isEdit?: boolean;
  isItem?: null | number;
  buttonType?: CheckListType;
}

const types: CheckListType[] = [CheckListType.Single, CheckListType.GroupItem];

const CheckListModal = ({
  defaultValues,
  isEdit,
  buttonType,
  isItem = null,
  ...props
}: Props) => {
  const { logbooks, fetchChecklists, checklists } = useChecklistsContext();
  const [type, setType] = useState<CheckListType | null>(null);
  useEffect(() => {
    setType(defaultValues?.type || buttonType || CheckListType.Single);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [defaultValues]);
  const { lbid } = useParams();

  const [selectedGroups, setSelectedGroups] = useState<ICheckListItem[]>([]);

  const values = {
    ...defaultValues,
    desc:
      isItem !== null
        ? defaultValues?.list[isItem].desc
        : (defaultValues?.desc ?? ""),
    name:
      isItem !== null
        ? defaultValues?.list[isItem].name
        : (defaultValues?.name ?? ""),
  };

  values.list =
    isItem !== null
      ? [...defaultValues?.list[isItem].items]
      : defaultValues?.list
        ? [...defaultValues.list]
        : [];

  const [state, handlers] = useListState(ensureItemIds(values.list));

  const methods = useForm<ICheckListForm>({
    values,
  } as any);

  useEffect(() => {
    // HARD FIX FOR VERY WEIRD BEHAVIOR
    handlers.setState(ensureItemIds(values.list ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [isItem]);

  const { post, isLoading } = usePostApi();
  const groupChecklists: ICheckListItem[] = useMemo(
    () => checklists.filter(i => i.data.type === CheckListType.Group),
    [checklists],
  );
  const postRequest = async (params: Record<string, any>) => {
    await post({
      request: "save-checklist",
      body: { params },
      onSuccess: () => {
        fetchChecklists({ params: { lbid, archived: 0 } }, true);
        props.onClose();
      },
    });
  };
  const parseLbid = (lbid: string | string[]) =>
    lbid ? (lbid.includes("*") ? "*" : lbid) : "";

  const onSubmitSingle: SubmitHandler<ICheckListForm> = useCallback(
    async formData => {
      const { name, desc, sort, lbid } = formData;

      const list = state.filter((item: any) => !!item.text);
      if (!list || list.length === 0) {
        Notification("Error", "Please add items to the checklist", "red");
        return;
      }

      if (!lbid || lbid.length === 0) {
        Notification("Error", "Please select at least one logbook", "red");
        return;
      }

      const params: Record<string, any> = {
        name,
        desc,
        sort,
        lbid: parseLbid(lbid),
        data: { type: CheckListType.Single, list },
        yachtid: getYachtId(),
      };
      if (defaultValues?.cid) params.cid = defaultValues.cid;
      await postRequest(params);
      methods.reset();
      handlers.setState([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
    [defaultValues?.cid, fetchChecklists, methods, post, props],
  );

  const onSubmitGroup: SubmitHandler<ICheckListForm> = useCallback(
    async formData => {
      const { name, desc, sort, lbid } = formData;
      if (!lbid || lbid.length === 0) {
        Notification("Error", "Please select at least one logbook", "red");
        return;
      }
      const data = { type: "group", list: state ?? [] };
      const params: Record<string, any> = {
        name,
        desc,
        sort,
        lbid: parseLbid(lbid),
        data,
        yachtid: getYachtId(),
      };
      if (isItem !== null) {
        params.data.list = defaultValues?.list;
        params.data.list[isItem] = {
          name,
          desc,
          items: state,
        };
        params.name = defaultValues?.name ?? params.name;
        params.desc = defaultValues?.desc ?? params.desc;
      }
      if (defaultValues?.cid) params.cid = defaultValues.cid;
      await postRequest(params);
      methods.reset();
      handlers.setState([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
    [isItem, defaultValues, state],
  );

  const onSubmitChildren: SubmitHandler<ICheckListForm> = async formData => {
    const list = state.filter(item => !!item.text);
    if (!list || list.length === 0) {
      methods.setError("list", { message: "error" });
      return;
    }

    if (selectedGroups.length === 0) {
      Notification("Error", "Please select at least one group", "red");
      return;
    }

    for (const group of selectedGroups) {
      const params = {
        cid: group.cid,
        lbid: group.lbid,
        yachtid: getYachtId(),
        name: group.name,
        sort: group.sort,
        desc: group.description,
        data: {
          ...group.data,
          list: [
            ...group.data.list,
            {
              name: formData.name,
              // desc: formData.desc,
              items: list,
            },
          ],
        },
      };
      await postRequest(params);
    }
    methods.reset();
    handlers.setState([]);
  };
  const handleSelectGroup = (checked: boolean, group: ICheckListItem) => {
    if (checked) setSelectedGroups(prev => [...prev, group]);
    else setSelectedGroups(prev => prev.filter(g => g.cid !== group.cid));
  };

  const logbooksCheckboxProps = {
    name: "lbid" as const,
    control: methods.control,
    data: logbooks,
    disabled: false,
    grouped: true,
    searchable: true,
    noScroll: true,
    full: true,
    wrapperStyles: { marginLeft: "8px" },
    labelStyles: { padding: "0", margin: "0 4px 0 0" },
    scrollAreaStyle: { paddingRight: 12 },
  };

  const renderLogbooksSidebar = (submitPt = 12) => (
    <Grid.Col span={4} className={styles.col}>
      <Flex
        direction="column"
        h="100%"
        w="100%"
        className={styles.logbooksPanel}>
        <Divider w="100%" my="md" label="Logbooks" />
        <div className={styles.logbooksList}>
          <CheckboxGroup {...logbooksCheckboxProps} />
        </div>
        <Group justify="flex-end" pt={submitPt}>
          <Button type="submit" loading={isLoading}>
            Submit
          </Button>
        </Group>
      </Flex>
    </Grid.Col>
  );

  const renderForm = () => {
    switch (type) {
      case CheckListType.Single:
        return (
          <FormProvider {...methods}>
            <form
              className={styles.form}
              onSubmit={methods.handleSubmit(onSubmitSingle)}>
              <Grid
                px={10}
                classNames={{ root: styles.grid, inner: styles.gridInner }}>
                <Grid.Col span={8} className={styles.col}>
                  <Flex direction="column" h="100%">
                    <div>
                      <Divider w="100%" mt="md" mb={10} label="Details" />
                      <Flex align="center" w="100%" gap="md" mb={8}>
                        <TextInput
                          {...methods.register("name")}
                          label="Name"
                          required
                          style={{ flex: 1 }}
                        />
                        <Select
                          label="Checklist type"
                          placeholder="Pick value"
                          value={type}
                          data={types}
                          disabled={isEdit}
                          onChange={v => setType(v as CheckListType)}
                          w={140}
                        />
                      </Flex>
                      <Textarea
                        {...methods.register("desc")}
                        label="Description"
                      />
                    </div>
                    <CheckListFieldArray items={state} handlers={handlers} />
                  </Flex>
                </Grid.Col>
                {renderLogbooksSidebar()}
              </Grid>
            </form>
          </FormProvider>
        );
      case CheckListType.Group:
        return (
          <FormProvider {...methods}>
            <form
              className={styles.form}
              onSubmit={methods.handleSubmit(onSubmitGroup)}>
              <Grid
                px={10}
                classNames={{ root: styles.grid, inner: styles.gridInner }}>
                <Grid.Col
                  span={isItem === null ? 8 : 12}
                  className={styles.col}>
                  <Flex direction="column" h="100%">
                    <Divider w="100%" mt="md" mb={10} label="Details" />
                    <TextInput
                      {...methods.register("name")}
                      label="Name"
                      required
                      style={{ marginBottom: "8px" }}
                    />
                    {isItem === null && (
                      <Textarea
                        {...methods.register("desc")}
                        label="Description"
                      />
                    )}
                    {isItem !== null && (
                      <>
                        <CheckListFieldArray
                          items={state}
                          handlers={handlers}
                        />
                        <Group justify="flex-end" pt={10} mt="auto">
                          <Button type="submit" loading={isLoading}>
                            Submit
                          </Button>
                        </Group>
                      </>
                    )}
                  </Flex>
                </Grid.Col>
                {isItem === null && renderLogbooksSidebar(10)}
              </Grid>
            </form>
          </FormProvider>
        );
      case CheckListType.GroupItem:
        return (
          <FormProvider {...methods}>
            <form
              className={styles.form}
              onSubmit={methods.handleSubmit(onSubmitChildren)}>
              <Grid
                px={10}
                classNames={{ root: styles.grid, inner: styles.gridInner }}>
                <Grid.Col span={8} className={styles.col}>
                  <Flex direction="column" h="100%">
                    <Divider w="100%" mt="md" mb={10} label="Details" />
                    <Flex align="center" w="100%" gap="md" mb={8}>
                      <TextInput
                        {...methods.register("name")}
                        label="Name"
                        required
                        style={{ flex: 1 }}
                      />
                      <Select
                        label="Checklist type"
                        placeholder="Pick value"
                        value={type}
                        data={types}
                        disabled={isEdit}
                        onChange={v => setType(v as CheckListType)}
                        w={140}
                      />
                    </Flex>
                    {/* <Textarea {...methods.register("desc")} label="Description" /> */}

                    <CheckListFieldArray items={state} handlers={handlers} />
                  </Flex>
                </Grid.Col>
                <Grid.Col span={4} className={styles.col}>
                  <Flex direction="column" h="100%">
                    <Text pt="md">Add to groups: </Text>
                    {groupChecklists.length === 0 ? (
                      <Text c="dimmed" size="sm" mt="sm" flex={1}>
                        No checklist groups yet. <br />
                        Create one first using "Add Group +".
                      </Text>
                    ) : (
                      <ScrollArea w="100%" style={{ flex: 1 }}>
                        {groupChecklists.map((g, i) => (
                          <ChecklistGroupItem
                            key={`group-select-${i}`}
                            {...g}
                            onChange={handleSelectGroup}
                          />
                        ))}
                      </ScrollArea>
                    )}
                    <Group pt={10}>
                      <Flex direction="column" flex={1}>
                        <AddCheckListButton
                          title="Add Group +"
                          type={CheckListType.Group}
                        />
                      </Flex>
                      <Button
                        type="submit"
                        loading={isLoading}
                        disabled={groupChecklists.length === 0}>
                        Submit
                      </Button>
                    </Group>
                  </Flex>
                </Grid.Col>
              </Grid>
            </form>
          </FormProvider>
        );
      default:
        return null;
    }
  };
  return (
    <Modal
      {...props}
      classNames={{
        content: styles.modalContent,
        body: styles.modalBody,
      }}
      onClose={() => {
        methods.reset();
        handlers.setState([]);
        props.onClose();
      }}
      title={type !== CheckListType.Group ? "Checklist" : "Group"}
      size="80%">
      {renderForm()}
    </Modal>
  );
};

export default CheckListModal;
